import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause, RotateCcw } from 'lucide-react';
import type { DemoStep, DemoAction } from '@/api/endpoints';

interface Props {
  steps: DemoStep[];
  open: boolean;
  onClose: () => void;
}

interface CursorState {
  x: number;
  y: number;
  clicking: boolean;
  transitionMs: number;
}

const CLICK_PAUSE_MS = 500;
const DEFAULT_MOVE_MS = 1000;

const DemoPlayer: React.FC<Props> = ({ steps, open, onClose }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [actionIdx, setActionIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [cursor, setCursor] = useState<CursorState>({
    x: 0, y: 0, clicking: false, transitionMs: 0,
  });
  const [highlight, setHighlight] = useState<[number, number, number, number] | null>(null);
  const [imgSize, setImgSize] = useState({ w: 1, h: 1 });
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const step = steps[stepIdx];

  useEffect(() => {
    if (!open) {
      clearTimer();
      setStepIdx(0);
      setActionIdx(0);
      setPlaying(true);
      setCursor({ x: 0, y: 0, clicking: false, transitionMs: 0 });
      setHighlight(null);
    }
  }, [open]);

  const runAction = useCallback(
    (action: DemoAction, onDone: () => void) => {
      if (action.type === 'move') {
        const [x, y] = action.to;
        const duration = action.duration ?? DEFAULT_MOVE_MS;
        setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: duration }));
        timerRef.current = setTimeout(onDone, duration + 50);
      } else if (action.type === 'click') {
        const [x, y] = action.at;
        setCursor((c) => ({ ...c, x, y, clicking: true, transitionMs: 150 }));
        timerRef.current = setTimeout(() => {
          setCursor((c) => ({ ...c, clicking: false }));
          onDone();
        }, CLICK_PAUSE_MS);
      } else if (action.type === 'wait') {
        timerRef.current = setTimeout(onDone, action.duration);
      } else if (action.type === 'type') {
        const [x, y] = action.at;
        setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: 300 }));
        const total = Math.max(600, action.text.length * 80);
        timerRef.current = setTimeout(onDone, total);
      } else if (action.type === 'highlight') {
        setHighlight(action.box);
        const duration = action.duration ?? 1500;
        timerRef.current = setTimeout(() => {
          setHighlight(null);
          onDone();
        }, duration);
      } else {
        onDone();
      }
    },
    [],
  );

  useEffect(() => {
    if (!open || !playing || !step) return;
    const action = step.actions[actionIdx];
    if (!action) {
      // step finished
      clearTimer();
      timerRef.current = setTimeout(() => {
        if (stepIdx < steps.length - 1) {
          setStepIdx(stepIdx + 1);
          setActionIdx(0);
        } else {
          setPlaying(false);
        }
      }, 1200);
      return clearTimer;
    }
    runAction(action, () => {
      setActionIdx((i) => i + 1);
    });
    return clearTimer;
  }, [open, playing, step, actionIdx, stepIdx, steps.length, runAction]);

  const goToStep = (idx: number) => {
    clearTimer();
    setStepIdx(idx);
    setActionIdx(0);
    setCursor({ x: 0, y: 0, clicking: false, transitionMs: 0 });
    setHighlight(null);
    setPlaying(true);
  };

  const restart = () => goToStep(stepIdx);
  const next = () => stepIdx < steps.length - 1 && goToStep(stepIdx + 1);
  const prev = () => stepIdx > 0 && goToStep(stepIdx - 1);

  const onImgLoad = () => {
    if (imgRef.current) {
      setImgSize({
        w: imgRef.current.naturalWidth,
        h: imgRef.current.naturalHeight,
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stepIdx, steps.length]);

  if (!open || !step) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative flex flex-col w-full max-w-5xl max-h-[90vh] rounded-2xl border border-line bg-zinc-950 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-line">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs uppercase tracking-wider text-zinc-500">
              Видеогайд
            </span>
            <span className="text-sm font-medium text-fg-primary truncate">
              Шаг {stepIdx + 1} из {steps.length}
              {step.caption && ` — ${step.caption}`}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-fill-2 text-zinc-400 hover:text-fg-primary transition-colors"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-zinc-900">
          <div
            className="relative inline-block max-w-full"
            style={{ lineHeight: 0 }}
          >
            <img
              ref={imgRef}
              src={step.imageUrl}
              alt={step.caption || `Шаг ${stepIdx + 1}`}
              onLoad={onImgLoad}
              className="block max-w-full max-h-[65vh] rounded-lg"
            />

            <svg
              viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full pointer-events-none"
            >
              {highlight && (
                <rect
                  x={highlight[0]}
                  y={highlight[1]}
                  width={highlight[2]}
                  height={highlight[3]}
                  fill="rgba(255, 230, 0, 0.15)"
                  stroke="#ffe600"
                  strokeWidth={3}
                />
              )}
            </svg>

            <div
              className="absolute pointer-events-none"
              style={{
                left: `${(cursor.x / imgSize.w) * 100}%`,
                top: `${(cursor.y / imgSize.h) * 100}%`,
                transform: 'translate(-4px, -2px)',
                transition: `left ${cursor.transitionMs}ms cubic-bezier(0.25, 0.1, 0.25, 1), top ${cursor.transitionMs}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }}>
                <path
                  d="M5 3L19 12L12 13L9 20L5 3Z"
                  fill="white"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
              </svg>
              {cursor.clicking && (
                <span
                  className="absolute rounded-full"
                  style={{
                    left: 0,
                    top: 0,
                    width: 40,
                    height: 40,
                    transform: 'translate(-20px, -20px)',
                    background: 'rgba(255, 82, 82, 0.25)',
                    border: '2px solid #ff5252',
                    animation: 'demo-ripple 0.5s ease-out',
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-line bg-zinc-950">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => goToStep(i)}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i === stepIdx
                    ? 'bg-white'
                    : i < stepIdx
                      ? 'bg-fill-2'
                      : 'bg-fill-2'
                }`}
                aria-label={`Перейти к шагу ${i + 1}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={prev}
              disabled={stepIdx === 0}
              className="p-2 rounded-lg hover:bg-fill-2 text-zinc-300 hover:text-fg-primary disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              aria-label="Предыдущий"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => setPlaying((p) => !p)}
              className="p-2 rounded-lg hover:bg-fill-2 text-zinc-300 hover:text-fg-primary transition-colors"
              aria-label={playing ? 'Пауза' : 'Играть'}
            >
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
            <button
              onClick={restart}
              className="p-2 rounded-lg hover:bg-fill-2 text-zinc-300 hover:text-fg-primary transition-colors"
              aria-label="Повторить"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
            <button
              onClick={next}
              disabled={stepIdx === steps.length - 1}
              className="p-2 rounded-lg hover:bg-fill-2 text-zinc-300 hover:text-fg-primary disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
              aria-label="Следующий"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes demo-ripple {
          0% { transform: translate(-20px, -20px) scale(0.2); opacity: 1; }
          100% { transform: translate(-20px, -20px) scale(1.4); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default DemoPlayer;
